import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { Badge } from './badge';
import { cn } from '../../lib/utils';
import { toCanberraDateKey } from '../../utils/dateFormatter';

// Build a YYYY-MM-DD key from a Date's own local fields only - never via
// toISOString(), which converts to UTC first and rolls positive-offset
// timezones (e.g. Canberra, UTC+10/+11) back onto the previous day.
const toLocalDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Status colors
const STATUS_COLORS = {
  'invited': { bg: 'bg-blue-100', text: 'text-blue-800', hover: 'hover:bg-blue-200', border: 'border-blue-200' },
  'in_progress': { bg: 'bg-indigo-100', text: 'text-indigo-800', hover: 'hover:bg-indigo-200', border: 'border-indigo-200' },
  'submitted': { bg: 'bg-purple-100', text: 'text-purple-800', hover: 'hover:bg-purple-200', border: 'border-purple-200' },
  'pending_outcome': { bg: 'bg-orange-100', text: 'text-orange-800', hover: 'hover:bg-orange-200', border: 'border-orange-200' },
  'successful': { bg: 'bg-green-100', text: 'text-green-800', hover: 'hover:bg-green-200', border: 'border-green-200' },
  'unsuccessful': { bg: 'bg-red-100', text: 'text-red-800', hover: 'hover:bg-red-200', border: 'border-red-200' },
};

export const RFQCalendar = ({ rfqs = [], onRFQClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('monthly'); // 'weekly' or 'monthly'

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Excluded statuses
  const EXCLUDED_STATUSES = ['not_invited', 'inclusion_requested', 'request_declined', 'not_interested'];

  // Get calendar range based on view mode
  const { startDate, endDate, firstDayOfWeek, calendarDays: daysInView } = useMemo(() => {
    if (viewMode === 'weekly') {
      // Get current week (Sunday to Saturday)
      const curr = new Date(currentDate);
      const firstDay = curr.getDate() - curr.getDay();
      const start = new Date(curr.setDate(firstDay));
      start.setHours(0, 0, 0, 0);

      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      end.setHours(23, 59, 59, 999);

      return {
        startDate: start,
        endDate: end,
        firstDayOfWeek: 0,
        calendarDays: 7
      };
    } else {
      // Monthly view
      const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      return {
        startDate: firstDayOfMonth,
        endDate: lastDayOfMonth,
        firstDayOfWeek: firstDayOfMonth.getDay(),
        calendarDays: lastDayOfMonth.getDate()
      };
    }
  }, [currentDate, viewMode]);

  // Filter RFQs by excluded statuses and group by date
  const rfqsByDate = useMemo(() => {
    const dateMap = {};

    rfqs
      .filter(rfq =>
        !EXCLUDED_STATUSES.includes(rfq.status) &&
        rfq.client_close_datetime
      )
      .forEach(rfq => {
        // Derive the closing calendar date from Canberra-local components, not by
        // round-tripping through UTC (see toCanberraDateKey / toLocalDateKey above).
        const dateKey = toCanberraDateKey(rfq.client_close_datetime);
        if (!dateKey) return;
        const [y, m, d] = dateKey.split('-').map(Number);
        // A plain local Date built from those components, used only for range
        // comparisons against startDate/endDate (also local-midnight Dates) -
        // never serialized via toISOString.
        const closingDate = new Date(y, m - 1, d);

        // Filter by current view range
        if (viewMode === 'weekly') {
          if (closingDate >= startDate && closingDate <= endDate) {
            if (!dateMap[dateKey]) {
              dateMap[dateKey] = [];
            }
            dateMap[dateKey].push(rfq);
          }
        } else {
          // Monthly view - only show RFQs in current month
          if (
            closingDate.getMonth() === currentDate.getMonth() &&
            closingDate.getFullYear() === currentDate.getFullYear()
          ) {
            if (!dateMap[dateKey]) {
              dateMap[dateKey] = [];
            }
            dateMap[dateKey].push(rfq);
          }
        }
      });

    return dateMap;
  }, [rfqs, currentDate, viewMode, startDate, endDate]);

  // Navigation functions
  const goToPrevious = () => {
    if (viewMode === 'weekly') {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() - 7);
      setCurrentDate(newDate);
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    }
  };

  const goToNext = () => {
    if (viewMode === 'weekly') {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 7);
      setCurrentDate(newDate);
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Format status label
  const formatStatus = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Build calendar grid
  const calendarCells = [];

  if (viewMode === 'weekly') {
    // Weekly view - show 7 days in a row
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      date.setHours(0, 0, 0, 0);

      const dateKey = toLocalDateKey(date);
      const dayRFQs = rfqsByDate[dateKey] || [];
      const isToday = date.getTime() === today.getTime();
      const isPast = date < today;

      calendarCells.push(
        <div
          key={i}
          className={cn(
            "min-h-[150px] border border-gray-100 p-2 relative",
            isToday && "bg-blue-50 border-blue-300 border-2",
            isPast && !isToday && "bg-gray-50/50"
          )}
        >
          <div className="flex items-center justify-between mb-2">
            <span
              className={cn(
                "text-sm font-medium",
                isToday && "bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
              )}
            >
              {date.getDate()}
            </span>
          </div>

          {/* Weekly view: Show detailed RFQ cards */}
          <div className="space-y-1.5">
            {dayRFQs.map((rfq) => {
              const statusColor = STATUS_COLORS[rfq.status] || STATUS_COLORS['invited'];
              return (
                <div
                  key={rfq.id}
                  onClick={() => onRFQClick?.(rfq)}
                  className={cn(
                    "text-[10px] p-1.5 rounded border cursor-pointer",
                    statusColor.bg,
                    statusColor.text,
                    statusColor.hover,
                    statusColor.border
                  )}
                >
                  <div className="font-medium truncate">{rfq.ref_code}</div>
                  <div className="truncate text-[9px] opacity-90">{rfq.title}</div>
                  <div className="truncate text-[9px] opacity-75 mt-0.5">
                    {rfq.client?.name || rfq.client_name || 'Unknown Client'}
                  </div>
                  <Badge variant="outline" className={cn("text-[8px] mt-1 px-1 py-0", statusColor.text)}>
                    {formatStatus(rfq.status)}
                  </Badge>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  } else {
    // Monthly view - add empty cells for days before month starts
    for (let i = 0; i < firstDayOfWeek; i++) {
      calendarCells.push(
        <div key={`empty-${i}`} className="min-h-[80px] sm:min-h-[100px] border border-gray-100 bg-gray-50/50" />
      );
    }

    // Add days of the month
    for (let day = 1; day <= daysInView; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      date.setHours(0, 0, 0, 0);

      const dateKey = toLocalDateKey(date);
      const dayRFQs = rfqsByDate[dateKey] || [];
      const isToday = date.getTime() === today.getTime();
      const isPast = date < today;

      calendarCells.push(
        <div
          key={day}
          className={cn(
            "min-h-[80px] sm:min-h-[100px] border border-gray-100 p-1 sm:p-2 relative overflow-hidden",
            isToday && "bg-blue-50 border-blue-300 border-2",
            isPast && !isToday && "bg-gray-50/50",
            dayRFQs.length > 0 && "hover:bg-gray-50"
          )}
        >
          <div className="flex items-start justify-between mb-1">
            <span
              className={cn(
                "text-xs sm:text-sm font-medium",
                isToday && "bg-blue-600 text-white rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-[10px] sm:text-xs",
                isPast && !isToday && "text-gray-400"
              )}
            >
              {day}
            </span>
          </div>

          {/* Monthly view: Show compact RFQ indicators */}
          <div className="space-y-0.5 sm:space-y-1">
            {dayRFQs.slice(0, 2).map((rfq) => {
              const statusColor = STATUS_COLORS[rfq.status] || STATUS_COLORS['invited'];
              return (
                <div
                  key={rfq.id}
                  onClick={() => onRFQClick?.(rfq)}
                  className={cn(
                    "text-[10px] sm:text-xs px-1 sm:px-1.5 py-0.5 rounded truncate cursor-pointer",
                    statusColor.bg,
                    statusColor.text,
                    statusColor.hover
                  )}
                  title={`${rfq.title} - ${rfq.client?.name || rfq.client_name || 'Unknown Client'} (${formatStatus(rfq.status)})`}
                >
                  <span className="hidden sm:inline">
                    {rfq.title.substring(0, 15)}{rfq.title.length > 15 ? '...' : ''}
                  </span>
                  <span className="sm:hidden">
                    {rfq.title.substring(0, 8)}{rfq.title.length > 8 ? '...' : ''}
                  </span>
                </div>
              );
            })}

            {dayRFQs.length > 2 && (
              <div className="text-[10px] sm:text-xs text-gray-500 px-1 sm:px-1.5">
                +{dayRFQs.length - 2}
              </div>
            )}
          </div>
        </div>
      );
    }
  }

  // Get header text
  const getHeaderText = () => {
    if (viewMode === 'weekly') {
      const end = new Date(startDate);
      end.setDate(end.getDate() + 6);

      if (startDate.getMonth() === end.getMonth()) {
        return `${MONTHS[startDate.getMonth()]} ${startDate.getDate()}-${end.getDate()}, ${startDate.getFullYear()}`;
      } else {
        return `${MONTHS[startDate.getMonth()]} ${startDate.getDate()} - ${MONTHS[end.getMonth()]} ${end.getDate()}, ${startDate.getFullYear()}`;
      }
    }
    return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base sm:text-lg">RFQ Calendar</CardTitle>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 border rounded-md p-0.5">
              <Button
                variant={viewMode === 'weekly' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('weekly')}
                className="h-7 px-2 text-xs"
              >
                Weekly
              </Button>
              <Button
                variant={viewMode === 'monthly' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('monthly')}
                className="h-7 px-2 text-xs"
              >
                Monthly
              </Button>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={goToToday}
              className="text-xs sm:text-sm h-7 sm:h-8"
            >
              Today
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={goToPrevious}
              className="h-7 w-7 sm:h-8 sm:w-8"
            >
              <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
            <div className="text-xs sm:text-sm font-medium min-w-[120px] sm:min-w-[180px] text-center">
              {getHeaderText()}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={goToNext}
              className="h-7 w-7 sm:h-8 sm:w-8"
            >
              <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
          </div>
        </div>

        <p className="text-xs sm:text-sm text-muted-foreground mt-2">
          Showing all RFQs except not invited, inclusion requested, request declined, and not interested.
        </p>
      </CardHeader>

      <CardContent className="px-2 sm:px-6">
        {/* Calendar Grid */}
        <div className="space-y-2 overflow-x-auto">
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-px min-w-[320px]">
            {DAYS.map((day) => (
              <div
                key={day}
                className="text-center text-[10px] sm:text-xs font-semibold text-gray-600 py-1 sm:py-2"
              >
                <span className="hidden sm:inline">{day}</span>
                <span className="sm:hidden">{day.substring(0, 1)}</span>
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 min-w-[320px]">
            {calendarCells}
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 space-y-2">
          <div className="text-[10px] sm:text-xs font-medium text-muted-foreground mb-1">Status Colors:</div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-muted-foreground">
            <div className="flex items-center gap-1 sm:gap-1.5">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-blue-600"></div>
              <span>Today</span>
            </div>
            {Object.entries(STATUS_COLORS).map(([status, colors]) => (
              <div key={status} className="flex items-center gap-1 sm:gap-1.5">
                <div className={cn("w-2.5 h-2.5 sm:w-3 sm:h-3 rounded", colors.bg)}></div>
                <span>{formatStatus(status)}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
